import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Clock, IndianRupee, CheckCircle, Award, Calendar } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const CourseDetail = () => {
  const { slug } = useParams();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, [slug]);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/public/training/courses/${slug}`);
      setCourse(response.data);
    } catch (error) {
      console.error('Failed to fetch course:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center min-h-screen">Loading...</div>;
  }

  if (!course) {
    return <div className="flex justify-center items-center min-h-screen">Course not found</div>;
  }

  // Structured data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": course.title,
    "description": course.description,
    "provider": {
      "@type": "Organization",
      "name": "ISDE Academy",
      "url": "https://isde.academy"
    },
    "offers": {
      "@type": "Offer",
      "price": course.price.replace(/[^\d]/g, ''),
      "priceCurrency": "INR",
      "availability": "https://schema.org/InStock"
    },
    "courseCode": course.standard_code,
    "timeRequired": course.duration,
    "educationalCredentialAwarded": course.certification
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO 
        title={course.meta_title || course.title}
        description={course.meta_description || `${course.description}. Duration: ${course.duration}. Certification included. ${course.course_type} training for ${course.standard_title}.`}
        keywords={course.meta_keywords || `${course.standard_code} ${course.course_type}, ISO training, certification course, ${course.standard_title}`}
        canonical={`/training/course/${slug}`}
        ogImage={course.og_image}
        structuredData={structuredData}
      />
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-green-600 to-teal-700 text-white py-16 mt-20">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-block bg-green-500 px-3 py-1 rounded-full text-sm mb-4">
              {course.standard_code}
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">{course.title}</h1>
            <p className="text-xl text-green-100 mb-6">{course.description}</p>
            <div className="flex flex-wrap gap-6 text-lg">
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                <span>{course.duration}</span>
              </div>
              <div className="flex items-center gap-2">
                <IndianRupee className="h-5 w-5" />
                <span className="font-bold">{course.price}</span>
              </div>
              <div className="flex items-center gap-2">
                <Award className="h-5 w-5" />
                <span>Certificate Included</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Course Objectives */}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                  Learning Objectives
                </h2>
                <ul className="space-y-3">
                  {course.objectives && course.objectives.map((objective, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="text-green-600 mt-1 text-xl">✓</span>
                      <span className="text-gray-700">{objective}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Course Outline */}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-2xl font-bold mb-4">Course Outline</h2>
                <div className="space-y-4">
                  {course.course_outline && course.course_outline.map((day, i) => (
                    <div key={i} className="border-l-4 border-blue-600 pl-4">
                      <h3 className="font-bold text-lg mb-2">Day {day.day}</h3>
                      <ul className="space-y-2">
                        {day.topics.map((topic, j) => (
                          <li key={j} className="text-gray-700 flex items-start gap-2">
                            <span className="text-blue-600 mt-1">•</span>
                            {topic}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Certification */}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                  <Award className="h-6 w-6 text-purple-600" />
                  Certification
                </h2>
                <p className="text-gray-700">{course.certification}</p>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-4 space-y-6">
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-2xl font-bold mb-4">Enroll Now</h3>
                  <div className="space-y-4">
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <p className="text-sm text-gray-600 mb-2">Course Fee</p>
                      <p className="text-3xl font-bold text-blue-600">{course.price}</p>
                    </div>
                    <Button
                      onClick={() => navigate('/training-calendar')}
                      className="w-full bg-green-600 hover:bg-green-700 text-white py-6 text-lg"
                    >
                      <Calendar className="h-5 w-5 mr-2" />
                      View Schedule & Register
                    </Button>
                    <Button
                      onClick={() => navigate('/contact')}
                      variant="outline"
                      className="w-full"
                    >
                      Request More Information
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <h3 className="font-bold mb-3">Course Details</h3>
                  <div className="space-y-3 text-sm">
                    <div>
                      <p className="text-gray-600">Duration</p>
                      <p className="font-semibold">{course.duration}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Course Type</p>
                      <p className="font-semibold">{course.course_type}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Standard</p>
                      <p className="font-semibold">{course.standard_title}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};